// Adapted from https://github.com/Azure-Samples/ms-identity-b2c-javascript-spa
// Create the main msalAgent instance
// configuration parameters are located at authConfig.js
// import axios from 'axios'
import b2cPolicies from '@/auth/policies'
import * as msal from '@azure/msal-browser'
import authConfig from '@/auth/authConfig'
import {useMsalStore} from '@/store/msal'
import {useApiStore} from '@/store/api'

const B2CStaffProviders = [
    'https://login.microsoftonline.com/34408998-73b4-4544-b57e-bbad0a3cd855/v2.0', // PaTTAN West
    'https://login.microsoftonline.com/7a6f63a3-7ba6-4e6d-b5db-e193e5b4bd49/v2.0', // PaTTAN Central
    'https://login.microsoftonline.com/db3429d1-64b8-4c56-89e2-33d3801721d8/v2.0', // PaTTAN East
]

// The MSAL agent is created lazily by initMsal() (called from main.js after
// app.use(pinia)), so this module is safe to static-import. msalAgent stays a
// live export — consumers read it after init has run (events-app#913).
export let msalAgent = null

// Pinia store resolved lazily so this module is safe to static-import before
// app.use(pinia) runs. Every `msalStore.x` call site continues to work.
function lazyStore(useStoreFn) {
    let resolved
    return new Proxy({}, {
        get(_, key) {
            if (!resolved) resolved = useStoreFn()
            return resolved[key]
        },
        set(_, key, value) {
            if (!resolved) resolved = useStoreFn()
            resolved[key] = value
            return true
        },
    })
}
const msalStore = lazyStore(useMsalStore)

// Add event callback to automatically update the access token in store.
// Wired up by initMsal() once the agent exists.
function registerTokenEventCallback() {
    msalAgent.addEventCallback((event) => {
        if (event.eventType === msal.EventType.ACQUIRE_TOKEN_SUCCESS) {
            if (event.payload?.accessToken && event.payload?.account) {
                console.log('Token acquired via MSAL event:', event.interactionType);
                msalStore.setAccessToken(event.payload.accessToken);
                const apiStore = useApiStore()

                // Set oid from account
                apiStore.oid = event.payload.account.localAccountId

                // Set isStaff based on identity provider
                if (event.payload.idTokenClaims?.idp) {
                    apiStore.isStaff = B2CStaffProviders.includes(event.payload.idTokenClaims.idp.toLowerCase())
                } else {
                    apiStore.isStaff = false
                }
            }
        }

        if (event.eventType === msal.EventType.ACQUIRE_TOKEN_FAILURE) {
            console.error('Token acquisition failed:', event.error);
            // Clear token on failure
            msalStore.setAccessToken('');

            // For silent failures, could trigger interactive login
            if (event.interactionType === msal.InteractionType.Silent) {
                console.warn('Silent token acquisition failed, may need user interaction');
            }
        }
    });
}

let accountId = ""

function setAccount(account) {
    accountId = account.homeAccountId
}

function selectAccount() {
    /**
     * See here for more info on account retrieval:
     * https://github.com/AzureAD/microsoft-authentication-library-for-js/blob/dev/lib/msal-common/docs/Accounts.md
     */

    const currentAccounts = msalAgent.getAllAccounts()

    if (currentAccounts.length < 1) {
        return
    } else if (currentAccounts.length > 1) {

        /**
         * Due to the way MSAL caches account objects, the auth response from initiating a user-flow
         * is cached as a new account, which results in more than one account in the cache. Here we make
         * sure we are selecting the account with homeAccountId that contains the sign-up/sign-in user-flow,
         * as this is the default flow the user initially signed-in with.
         */
        const accounts = currentAccounts.filter(account =>
            account.homeAccountId.toUpperCase().includes(b2cPolicies.names.signUpSignIn.toUpperCase())
            &&
            account.idTokenClaims
            &&
            account.idTokenClaims.iss.toUpperCase().includes(b2cPolicies.authorityDomain.toUpperCase())
            &&
            account.idTokenClaims.aud === authConfig.msalConfig.auth.clientId
            )

        if (accounts.length > 1) {
            // localAccountId identifies the entity for which the token asserts information.
            if (accounts.every(account => account.localAccountId === accounts[0].localAccountId)) {
                // All accounts belong to the same user
                setAccount(accounts[0])
            } else {
                // Multiple users detected. Logout all to be safe.
                signOut()
            }
        } else if (accounts.length === 1) {
            setAccount(accounts[0])
        }

    } else if (currentAccounts.length === 1) {
        setAccount(currentAccounts[0])
    }
}

// Create the MSAL agent and wire up its event callback + account selection.
// Called once from main.js after app.use(pinia); safe to call more than once.
export async function initMsal() {
    if (msalAgent) {
        return msalAgent
    }
    msalAgent = await msal.PublicClientApplication.createPublicClientApplication(authConfig.msalConfig)
    registerTokenEventCallback()
    selectAccount() // in case of page refresh
    return msalAgent
}

function handleResponse(response) {
    /**
     * To see the full list of response object properties, visit:
     * https://github.com/AzureAD/microsoft-authentication-library-for-js/blob/dev/lib/msal-browser/docs/request-response-object.md#response
     */

    if (response !== null) {
        setAccount(response.account)
    } else {
        selectAccount()
    }
}

function signIn() {
    /**
     * You can pass a custom request object below. This will override the initial configuration. For more information, visit:
     * https://github.com/AzureAD/microsoft-authentication-library-for-js/blob/dev/lib/msal-browser/docs/request-response-object.md#request
     */

     msalAgent.loginPopup(authConfig.loginRequest)
        .then(handleResponse)
        .catch(error => {
            console.log(error)
        })
}

function signOut() {

    /**
     * You can pass a custom request object below. This will override the initial configuration. For more information, visit:
     * https://github.com/AzureAD/microsoft-authentication-library-for-js/blob/dev/lib/msal-browser/docs/request-response-object.md#request
     */

    const logoutRequest = {
        postLogoutRedirectUri: authConfig.msalConfig.auth.redirectUri + '?signout=true',
        mainWindowRedirectUri: authConfig.msalConfig.auth.redirectUri + '?signout=true'
    }

    msalAgent.logoutPopup(logoutRequest)
}

/**
 * To initiate a B2C user-flow, simply make a login request using
 * the full authority string of that user-flow e.g.
 * https://fabrikamb2c.b2clogin.com/fabrikamb2c.onmicrosoft.com/B2C_1_edit_profile_v2
 */
async function editProfile() {

    const editProfileRequest = b2cPolicies.authorities.editProfile;
    editProfileRequest.loginHint = msalAgent.getAccountByHomeId(accountId).username;

    await msalAgent.loginPopup(editProfileRequest)
        .catch(error => {
            console.error(error)
        });
}
/*
async function updateIdentityProvider(userData) {
    // async call to api to set the last_identity_provider on the attendee that just logged in
    let apiStore = useApiStore()

    let identity_provider;
    if( userData.account.idTokenClaims.currentUserIdentities && userData.account.idTokenClaims.currentUserIdentities.length > 0){
        let identity_provider_raw = userData.account.idTokenClaims.currentUserIdentities[0].Issuer
        switch (true) {
            case /microsoft/g.test(identity_provider_raw):
                identity_provider = 'microsoft'
                break;
            case /google/g.test(identity_provider_raw):
                identity_provider = 'google'
                break;
            case /apple/g.test(identity_provider_raw):
                identity_provider = 'apple'
                break;
            default:
                identity_provider = 'unknown'
        }
    } else {
        identity_provider = 'local'
    }

    const endpoint = apiStore.buildUrl('/events/attendees/')
    const data = {last_identity_provider: identity_provider, id: userData.uniqueId}
    const headers = {Authorization: `Bearer ${userData.accessToken}`}
    await axios.patch(endpoint, data, {headers: headers}).catch((error) => console.error(error.response))
}
*/
export {editProfile, signIn, signOut}
